import { useTranslation } from '../i18n';
import {
  MessageCircle,
  ChevronDown,
  Wallet,
  Target,
  Hash,
  Repeat,
  Globe,
  LayoutDashboard,
  Briefcase,
  User,
  Users,
  Plane,
  Check,
  FileText,
  Sparkles,
  MessageSquare,
  Camera,
  Mic,
  Gift,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { analyticsEvents } from '../components/Analytics';
import { CookieBanner } from '../components/CookieConsent';
import { usePublicPlans } from '../hooks/usePublicPlans';
import { useAuthStore } from '../stores/authStore';

// Numero do WhatsApp (formato internacional sem +)
const WHATSAPP_NUMBER = '447934514016';

const formatPrice = (price: string, currency: string) => {
  const num = parseFloat(price);
  if (currency === 'BRL') {
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  }
  return num.toLocaleString('en-GB', { minimumFractionDigits: 2 });
};

const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case 'BRL': return 'R$';
    case 'GBP': return '£';
    case 'USD': return '$';
    default: return currency;
  }
};

const featureIcons: Record<string, React.ReactNode> = {
  wallet: <Wallet className="w-6 h-6" />,
  target: <Target className="w-6 h-6" />,
  hash: <Hash className="w-6 h-6" />,
  repeat: <Repeat className="w-6 h-6" />,
  globe: <Globe className="w-6 h-6" />,
  layout: <LayoutDashboard className="w-6 h-6" />,
};

const audienceIcons: Record<string, React.ReactNode> = {
  briefcase: <Briefcase className="w-8 h-8" />,
  user: <User className="w-8 h-8" />,
  users: <Users className="w-8 h-8" />,
  plane: <Plane className="w-8 h-8" />,
};

function LandingPage() {
  const { t, toggleLocale, isPortuguese } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const country = isPortuguese ? 'BR' : 'UK';
  const { data: plans } = usePublicPlans(country as 'BR' | 'UK');

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    isPortuguese ? 'Oi! Quero testar o KeepNota' : 'Hi! I want to try KeepNota'
  )}`;

  const handleWhatsAppClick = () => {
    analyticsEvents.clickWhatsApp();
  };

  const handleLanguageToggle = () => {
    toggleLocale();
    analyticsEvents.changeLanguage(isPortuguese ? 'en-GB' : 'pt-BR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md z-50 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
            <span className="font-bold text-xl text-gray-900 dark:text-white">KeepNota</span>
          </div>

          <div className="flex items-center gap-4">
            <a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-green-600 hidden sm:block">
              {t.nav.features}
            </a>
            <a href="#pricing" className="text-gray-600 dark:text-gray-300 hover:text-green-600 hidden sm:block">
              {t.nav.pricing}
            </a>
            <a href="#how-it-works" className="text-gray-600 dark:text-gray-300 hover:text-green-600 hidden sm:block">
              {t.nav.howItWorks}
            </a>

            {/* Language Toggle */}
            <button
              onClick={handleLanguageToggle}
              className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition"
            >
              {t.flag} {t.lang.split('-')[0].toUpperCase()}
            </button>

            <Link
              to={isAuthenticated ? "/app/dashboard" : "/auth"}
              className="text-gray-600 dark:text-gray-300 hover:text-green-600"
            >
              {isAuthenticated ? 'Dashboard' : t.nav.login}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            {t.hero.badge}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            {t.hero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick}
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-full text-lg transition shadow-lg hover:shadow-xl"
            >
              <MessageCircle className="w-6 h-6" />
              {t.hero.cta}
            </a>
            <span className="text-gray-500 dark:text-gray-400 text-sm">{t.hero.ctaSubtext}</span>
          </div>

          <p className="text-amber-600 dark:text-amber-400 text-sm font-medium mb-4">
            {t.hero.ukOnly}
          </p>

          {/* Trust indicator */}
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            {t.hero.trustedBy}
          </p>

          {/* Scroll indicator */}
          <div className="mt-12 animate-bounce">
            <ChevronDown className="w-8 h-8 mx-auto text-gray-400" />
          </div>
        </div>
      </section>

      {/* How it Works - 3 Input Methods */}
      <section id="how-it-works" className="py-20 px-4 bg-white dark:bg-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t.howItWorks.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t.howItWorks.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Text */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-8 text-center hover:shadow-lg transition">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-2xl mb-6">
                <MessageSquare className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                {t.howItWorks.step1.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t.howItWorks.step1.description}
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-2 text-sm font-mono text-green-600 dark:text-green-400">
                {t.howItWorks.step1.example}
              </div>
            </div>

            {/* Photo */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-8 text-center hover:shadow-lg transition">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl mb-6">
                <Camera className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                {t.howItWorks.step2.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t.howItWorks.step2.description}
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-2 text-sm font-mono text-blue-600 dark:text-blue-400">
                {t.howItWorks.step2.example}
              </div>
            </div>

            {/* Audio */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-8 text-center hover:shadow-lg transition">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-2xl mb-6">
                <Mic className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                {t.howItWorks.step3.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {t.howItWorks.step3.description}
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-2 text-sm font-mono text-purple-600 dark:text-purple-400">
                {t.howItWorks.step3.example}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t.features.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t.features.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.features.list.map((feature, i) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition border border-gray-100 dark:border-gray-700"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl text-green-600 dark:text-green-400 mb-4">
                  {featureIcons[feature.icon]}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                  {feature.description}
                </p>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-green-600 dark:text-green-400">
                  {feature.command}
                </code>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commands Quick Reference */}
      <section className="py-20 px-4 bg-gray-900 dark:bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3">
              {t.commands.title}
            </h2>
            <p className="text-gray-400">
              {t.commands.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {t.commands.list.map((item, i) => (
              <div
                key={i}
                className="bg-gray-800 rounded-lg p-4 border border-gray-700"
              >
                <code className="text-green-400 font-mono text-sm block mb-1">
                  {item.cmd}
                </code>
                <span className="text-gray-400 text-xs">
                  {item.desc}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience / Personas */}
      <section className="py-20 px-4 bg-white dark:bg-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t.audience.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t.audience.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.audience.personas.map((persona, i) => (
              <div
                key={i}
                className="text-center p-6"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-2xl text-green-600 dark:text-green-400 mb-4">
                  {audienceIcons[persona.icon]}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {persona.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {persona.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t.comparison.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t.comparison.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* vs Spreadsheet */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t.comparison.vsSpreadsheet.title}
              </h3>
              <ul className="space-y-3">
                {t.comparison.vsSpreadsheet.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 dark:text-gray-300 text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* vs Apps */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t.comparison.vsApps.title}
              </h3>
              <ul className="space-y-3">
                {t.comparison.vsApps.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 dark:text-gray-300 text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            {/* vs Manual */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t.comparison.vsManual.title}
              </h3>
              <ul className="space-y-3">
                {t.comparison.vsManual.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 dark:text-gray-300 text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Bonus - Secondary */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-2xl">
                <FileText className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t.taxBonus.title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t.taxBonus.subtitle}
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {t.taxBonus.points.map((point, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 bg-white dark:bg-gray-700 px-3 py-1 rounded-full text-sm text-gray-700 dark:text-gray-300"
                  >
                    <Check className="w-4 h-4 text-green-500" />
                    {point}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t.pricing.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t.pricing.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {plans?.map((plan) => {
              const isPopular = plan.isPopular || plan.type === 'pro';
              const currencySymbol = getCurrencySymbol(plan.currency);

              if (isPopular) {
                return (
                  <div key={plan.id} className="bg-green-500 rounded-2xl p-8 shadow-lg relative">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 text-sm font-bold px-4 py-1 rounded-full">
                      {t.pricing.pro.popular}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-4xl font-bold text-white">
                        {currencySymbol}{formatPrice(plan.priceMonthly, plan.currency)}
                      </span>
                      <span className="text-green-100">{t.pricing.monthly}</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-white">
                          <Check className="w-5 h-5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleWhatsAppClick}
                      className="block w-full text-center py-3 bg-white text-green-600 rounded-full font-semibold hover:bg-green-50 transition"
                    >
                      {t.pricing.cta}
                    </a>
                  </div>
                );
              }

              return (
                <div key={plan.id} className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {currencySymbol}{formatPrice(plan.priceMonthly, plan.currency)}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">{t.pricing.monthly}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Check className="w-5 h-5 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleWhatsAppClick}
                    className="block w-full text-center py-3 border-2 border-green-500 text-green-600 dark:text-green-400 rounded-full font-semibold hover:bg-green-50 dark:hover:bg-green-900/20 transition"
                  >
                    {t.pricing.cta}
                  </a>
                </div>
              );
            })}
          </div>

          <p className="text-center text-gray-500 dark:text-gray-400 text-sm mt-6">
            {t.pricing.trial}
          </p>
        </div>
      </section>

      {/* Referral */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-500 to-pink-500">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 mb-4">
                <Gift className="w-8 h-8 text-white" />
                <h2 className="text-2xl font-bold text-white">
                  {t.referral.title}
                </h2>
              </div>
              <p className="text-purple-100 mb-4">
                {t.referral.subtitle}
              </p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                {t.referral.tiers.map((tier, i) => (
                  <span
                    key={i}
                    className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm text-white"
                  >
                    {tier.range}: <strong>{tier.reward}</strong>
                  </span>
                ))}
              </div>
            </div>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick}
              className="inline-flex items-center gap-2 bg-white text-purple-600 font-semibold px-6 py-3 rounded-full hover:bg-purple-50 transition"
            >
              {t.referral.cta}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white dark:bg-gray-800">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t.faq.title}
          </h2>

          <div className="space-y-4">
            {t.faq.items.map((item, i) => (
              <details
                key={i}
                className="group bg-gray-50 dark:bg-gray-700/50 rounded-xl"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="font-semibold text-gray-900 dark:text-white">{item.q}</span>
                  <ChevronDown className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="px-6 pb-6 text-gray-600 dark:text-gray-300">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-green-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t.cta.title}</h2>
          <p className="text-green-100 mb-8">{t.cta.subtitle}</p>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleWhatsAppClick}
            className="inline-flex items-center gap-2 bg-white text-green-600 font-semibold px-8 py-4 rounded-full text-lg hover:bg-green-50 transition shadow-lg"
          >
            <MessageCircle className="w-6 h-6" />
            {t.cta.button}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="KeepNota" className="h-6 w-auto" />
                <span className="font-semibold text-white">KeepNota</span>
              </div>
              <p className="text-sm mb-2">
                {t.footer.tagline}
              </p>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">
                {isPortuguese ? 'Legal' : 'Legal'}
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/terms" className="hover:text-white transition">
                    {t.footer.terms}
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="hover:text-white transition">
                    {t.footer.privacy}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company Details (UK Compliance) */}
            <div>
              <h4 className="font-semibold text-white mb-4">
                {isPortuguese ? 'Empresa' : 'Company'}
              </h4>
              <address className="text-sm not-italic space-y-1">
                <p className="text-white">Fig & Rosa Ltd</p>
                <p>Company No. 14150103</p>
                <p>71-75 Shelton Street</p>
                <p>Covent Garden, London</p>
                <p>WC2H 9JQ, United Kingdom</p>
                <p className="mt-2">
                  <a href="mailto:hello@keepnota.com" className="hover:text-white transition">
                    hello@keepnota.com
                  </a>
                </p>
              </address>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 border-t border-gray-800 text-center text-sm">
            <p>{t.footer.copyright}</p>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieBanner />
    </div>
  );
}

export default LandingPage;
